// src/pages/university-admin/SupervisorApproval.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    CircularProgress,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    useMediaQuery,
    Stack,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Search,
    School,
    Work,
    CheckCircle,
    Cancel,
    Schedule,
    Visibility,
    Assignment,
    Verified,
    Pending,
    Info,
    Email,
    Phone,
    Flag,
    Person,
    Badge as BadgeIcon
} from '@mui/icons-material';
import useCustomToast from '../../hooks/useToast';
import {
    APISupervisorApprovalUpdateApproval,
    APISupervisorApprovalShow,
    APISupervisorProfileImage,
    APISupervisorApprovalByUniversity
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named InstitutionAdminBar.js
// but it now renders "Unit Research Lead Portal" as its visible title.
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const getInitials = (name) => {
    if (!name) return 'SP';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const SupervisorApproval = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [approvals, setApprovals] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedApproval, setSelectedApproval] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        try {
            await fetchApprovals();
        } catch (err) {
            setError("Failed to load approval data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchApprovals = async () => {
        const universityId = getUserDetails("universityId");
        const result = await apiService.listById(APISupervisorApprovalByUniversity, universityId);
        if (result.success && isMounted.current) {
            setApprovals(Array.isArray(result.data) ? result.data : []);
        } else {
            console.error("Failed to fetch approvals:", result.message);
        }
    };

    const handleApprovalToggle = async (approval) => {
        setActionLoading(true);
        try {
            const nextApprovedState = approval.approval_status !== 'approved';
            const formData = new FormData();
            formData.append('approval_id', approval.id);
            formData.append('is_approved', nextApprovedState ? '1' : '0');
            
            const result = await apiService.update(APISupervisorApprovalUpdateApproval, formData);
            
            if (result && result.success) {
                showToast("Success", "Approval status updated successfully", "success");
                await fetchApprovals();
            } else {
                showToast("Error", result.message || "Failed to update approval", "error");
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to update approval", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleOpenViewDialog = async (approval) => {
        setSelectedApproval(approval);
        setOpenViewDialog(true);
        
        try {
            const result = await apiService.show(APISupervisorApprovalShow, approval.id);
            if (result.success && result.data) {
                setSelectedApproval(result.data);
            }
        } catch (error) {
            console.error("Error fetching details:", error);
        }
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedApproval(null);
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'approved': return <CheckCircle />;
            case 'rejected': return <Cancel />;
            case 'pending_review': return <Schedule />;
            case 'under_review': return <Visibility />;
            case 'conditional_approval': return <Assignment />;
            default: return <Pending />;
        }
    };

    const getProfileImageUrl = (supervisor) => {
        if (supervisor?.profile_image) {
            return APISupervisorProfileImage + supervisor.profile_image;
        }
        return null;
    };

    const filteredApprovals = approvals.filter(approval => 
        approval.supervisor?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.supervisor?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.approval_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const pendingCount = approvals.filter(a => a.approval_status === 'pending_review' || a.approval_status === 'under_review').length;

    if (loading) {
        return <LoadingState message="Loading Supervisor Approvals..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Approvals" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <InstitutionAdminBar notificationCount={90} />
                    
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <Verified /> Supervisor Approval Queue
                                {pendingCount > 0 && (
                                    <Chip 
                                        label={`${pendingCount} Pending`} 
                                        color="warning" 
                                        size="small"
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                )}
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchAllData} 
                                sx={{ 
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Paper>

                    {/* Search Bar */}
                    <Paper sx={{ 
                        mb: 3, 
                        p: { xs: 1.5, sm: 2 }, 
                        display: 'flex', 
                        alignItems: 'center',
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 2
                    }}>
                        <Search sx={{ mx: 1, color: 'text.secondary' }} />
                        <TextField 
                            fullWidth 
                            placeholder="Search by name, email, or ID..." 
                            variant="standard" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            InputProps={{ disableUnderline: true }}
                            size={isMobile ? "small" : "medium"}
                        />
                    </Paper>
                </Box>
                
                {/* Approvals Table */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto',
                    backgroundColor: 'white',
                }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 600, sm: 700, md: 900 } }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Applicant</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Academic Title</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>University</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Submitted</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Profile Completion</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Approval Toggle</TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredApprovals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((approval) => {
                                    const avatarColor = stringToColor(approval.supervisor?.name);
                                    const initials = getInitials(approval.supervisor?.name);
                                    const profileImage = getProfileImageUrl(approval.supervisor);
                                    const isApproved = approval.approval_status === 'approved';
                                    
                                    return (
                                        <TableRow key={approval.id} hover>
                                            <TableCell>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 2,
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    textAlign: { xs: 'center', sm: 'left' }
                                                }}>
                                                    <Avatar 
                                                        src={profileImage}
                                                        sx={{ 
                                                            width: { xs: 40, sm: 48 }, 
                                                            height: { xs: 40, sm: 48 },
                                                            bgcolor: !profileImage ? avatarColor : 'transparent',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {!profileImage && initials}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ 
                                                            fontWeight: 600,
                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                        }}>
                                                            {approval.supervisor?.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Email sx={{ fontSize: 12 }} />
                                                            {approval.supervisor?.email}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={approval.supervisor?.academic_title || 'N/A'} 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                {approval.supervisor?.university}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                {formatCreatedAtDate(approval.submission_date)}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <LinearProgress 
                                                        variant="determinate" 
                                                        value={approval.verification?.progress || 0} 
                                                        sx={{ width: { xs: 60, sm: 80 }, height: 6, borderRadius: 3 }} 
                                                    />
                                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                        {approval.verification?.progress || 0}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={isApproved}
                                                            onChange={() => handleApprovalToggle(approval)}
                                                            color="success"
                                                            disabled={actionLoading}
                                                        />
                                                    }
                                                    label={isApproved ? "Approved" : "Pending"}
                                                    sx={{ '& .MuiFormControlLabel-label': { fontSize: { xs: '0.65rem', sm: '0.75rem' } } }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                                                    <Tooltip title="View Details">
                                                        <IconButton size="small" onClick={() => handleOpenViewDialog(approval)}>
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filteredApprovals.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No supervisor approvals found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredApprovals.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { 
                            setRowsPerPage(parseInt(e.target.value, 10)); 
                            setPage(0); 
                        }}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: { xs: '0.7rem', sm: '0.875rem' }
                            }
                        }}
                    />
                </Paper>

                {/* View Details Dialog */}
                <Dialog 
                    open={openViewDialog} 
                    onClose={handleCloseViewDialog} 
                    maxWidth="md" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            maxHeight: '90vh'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Verified color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Supervisor Application Details
                                </Typography>
                            </Box>
                            <Chip 
                                icon={selectedApproval && getStatusIcon(selectedApproval.approval_status)} 
                                label={selectedApproval?.formatted_approval_status} 
                                color={selectedApproval?.approval_status_color}
                                size="small"
                            />
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedApproval && selectedApproval.supervisor && (
                            <Box sx={{ pt: 2 }}>
                                {/* Profile Header */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2, 
                                    mb: 3,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    textAlign: { xs: 'center', sm: 'left' }
                                }}>
                                    <Avatar 
                                        src={getProfileImageUrl(selectedApproval.supervisor)}
                                        sx={{ 
                                            width: { xs: 64, sm: 80 }, 
                                            height: { xs: 64, sm: 80 },
                                            bgcolor: !getProfileImageUrl(selectedApproval.supervisor) ? stringToColor(selectedApproval.supervisor.name) : 'transparent',
                                            fontSize: '2rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {!getProfileImageUrl(selectedApproval.supervisor) && getInitials(selectedApproval.supervisor.name)}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 'bold' }}>
                                            {selectedApproval.supervisor.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Email sx={{ fontSize: 14 }} />
                                            {selectedApproval.supervisor.email}
                                        </Typography>
                                        <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                            <Work fontSize="small" />
                                            {selectedApproval.supervisor.academic_title || 'Academic Title Not Set'}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                {/* Personal Info */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    Personal Information
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Person fontSize="small" color="action" />
                                            <strong>Full Name:</strong> {selectedApproval.supervisor.name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Phone fontSize="small" color="action" />
                                            <strong>Phone:</strong> {selectedApproval.supervisor.contact_phone || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Flag fontSize="small" color="action" />
                                            <strong>Nationality:</strong> {selectedApproval.supervisor.nationality || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BadgeIcon fontSize="small" color="action" />
                                            <strong>Registration ID:</strong> {selectedApproval.supervisor.registration_id}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <School fontSize="small" color="action" />
                                            <strong>University:</strong> {selectedApproval.supervisor.university || 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                
                                {/* Research Expertise */}
                                {selectedApproval.supervisor.research_expertise && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            Research Expertise
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            {selectedApproval.supervisor.research_expertise}
                                        </Typography>
                                    </>
                                )}
                                
                                {/* Biography */}
                                {selectedApproval.supervisor.biography && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            Biography
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                {selectedApproval.supervisor.biography}
                                            </Typography>
                                        </Paper>
                                    </>
                                )}
                                
                                {/* Verification Progress */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, mt: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    Verification Progress
                                </Typography>
                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Chip 
                                            icon={<School />} 
                                            label={`Qualifications: ${selectedApproval.verification?.qualification?.formatted_status || 'Not Started'}`} 
                                            color={selectedApproval.verification?.qualification?.color || 'default'} 
                                            size="small"
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Chip 
                                            icon={<Work />} 
                                            label={`Experience: ${selectedApproval.verification?.experience?.formatted_status || 'Not Started'}`} 
                                            color={selectedApproval.verification?.experience?.color || 'default'} 
                                            size="small"
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseViewDialog} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default SupervisorApproval;